import { TKV2_ORDER_ATTACHMENT_API } from '@aloft/toolkit-shared';
import { useQueryClient } from '@tanstack/react-query';

import { useEffect } from 'react';

import {
  QueryOperation,
  useUpdateCompPropertyData,
  useUpdateSubjectPropertyData,
} from '../../hooks/api/toolkitComponents.ts';
import { queryKeyFn } from '../../hooks/api/toolkitContext.ts';
import { useEdgeTrigger } from '../../hooks/useEdgeTrigger.ts';
import { mutationComplete } from '../../redux/features/propertyDataSlice.ts';
import { selectPropertyDataById } from '../../redux/features/selectors/propertyDataSelectors/propertyDataSelectors.ts';
import { useAppDispatch, useAppSelector } from '../../redux/hooks.ts';
import { CompPropertyDataState, SubjectPropertyDataState } from '../../redux/types.ts';
import { DEFAULT_SYNC_DRIVER_DELAY_MS } from './consts.ts';

export const PropertyDataSyncDriver = ({ orderID, id }: { orderID: string; id: string }) => {
  const dispatch = useAppDispatch();
  const propertyDataState = useAppSelector((state) => selectPropertyDataById(state, id));

  const { editRevision = 0, serverRevision = 0 } = propertyDataState?.syncState ?? {};

  const { mutate: subjectMutate } = useUpdateSubjectPropertyData();
  const { mutate: compMutate } = useUpdateCompPropertyData();

  const queryClient = useQueryClient();
  const variables = {
    pathParams: { orderID },
  };

  const queryKeyParams: QueryOperation =
    id === 'subject'
      ? {
          path: '/api/v2/orders/{orderID}/attachments/subjectdata',
          operationId: 'getSubjectData',
          variables,
        }
      : {
          path: '/api/v2/orders/{orderID}/attachments/compdata',
          operationId: 'getAllCompData',
          variables,
        };

  const onSuccess = () => {
    queryClient
      .invalidateQueries({ queryKey: [queryKeyFn(queryKeyParams)] })
      .then(() => {
        dispatch(
          mutationComplete({
            id,
            editRevision,
            serverRevision: editRevision,
            status: 'success',
          }),
        );
      })
      .catch((error) => {
        console.error('Error invalidating queries:', error);
      });
  };

  const onError = () => {
    dispatch(
      mutationComplete({
        id,
        editRevision,
        serverRevision,
        status: 'error',
      }),
    );
  };

  const mutate = () => {
    if (id === 'subject') {
      subjectMutate(
        {
          pathParams: { orderID },
          body: TKV2_ORDER_ATTACHMENT_API.SUBJECT_PROPERTY_DATA.PUT.REQUEST.BODY.parse(
            (propertyDataState as SubjectPropertyDataState)?.subjectData,
          ) as SubjectPropertyDataState['subjectData'],
        },
        {
          onSuccess,
          onError,
        },
      );
    } else {
      compMutate(
        {
          pathParams: { orderID, compID: id },
          body: TKV2_ORDER_ATTACHMENT_API.COMP_PROPERTY_DATA.PUT.REQUEST.BODY.parse(
            (propertyDataState as CompPropertyDataState)?.compData,
          ) as CompPropertyDataState['compData'],
        },
        {
          onSuccess,
          onError,
        },
      );
    }
  };

  const mutationTrigger = useEdgeTrigger({
    condition: editRevision > serverRevision,
    delayMs: DEFAULT_SYNC_DRIVER_DELAY_MS,
    action: mutate,
  });

  useEffect(() => {
    mutationTrigger.restartTimer();
  }, [editRevision, mutationTrigger]);

  return null;
};
